import { Box, Download, Eye } from 'lucide-react';
import { type MouseEvent, useState } from 'react';
import type { Artifact } from '../lib/artifacts';
import { cn } from '../lib/cn';
import { downloadWorkspaceFile } from '../lib/files';

type Props = {
  artifact: Artifact;
  onOpen: (id: string) => void;
  isOpen: boolean;
};

/**
 * Card shown in the message stream in place of a long fenced block
 * that qualifies as an artifact. Clicking it opens the side panel
 * focused on this artifact. Matches claude.ai's behaviour where
 * the block inlines a small "open in preview" widget instead of
 * spilling 200 lines of HTML into the chat.
 */
export function ArtifactCard({ artifact, onOpen, isOpen }: Props) {
  const lineCount = artifact.body ? artifact.body.split('\n').length : 0;
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState<string | null>(null);

  async function onDownload(e: MouseEvent<HTMLButtonElement>): Promise<void> {
    e.stopPropagation();
    e.preventDefault();
    setDlError(null);
    if (artifact.relativePath) {
      setDownloading(true);
      try {
        await downloadWorkspaceFile(artifact.relativePath);
      } catch (err) {
        setDlError(err instanceof Error ? err.message : 'Не удалось скачать файл');
      } finally {
        setDownloading(false);
      }
      return;
    }
    // Fenced-block artifact — no workspace file, save the body blob.
    const mime =
      artifact.kind === 'html'
        ? 'text/html;charset=utf-8'
        : artifact.kind === 'svg'
          ? 'image/svg+xml'
          : 'text/plain;charset=utf-8';
    const blob = new Blob([artifact.body], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = artifact.title ?? `artifact.${artifact.lang || 'txt'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div
      className={cn(
        'group my-3 flex w-full items-center gap-3 rounded-xl border border-border bg-subtle/50 px-3 py-2.5 transition hover:bg-subtle',
        isOpen && 'border-primary/60 bg-bubble',
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(artifact.id)}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 text-left"
        title="Открыть в панели"
      >
        <Box size={18} className="shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium">
            {artifact.title ?? `Артефакт: ${artifact.kind}`}
          </div>
          <div className="text-[12px] text-muted-foreground">
            <span className="font-mono">{artifact.lang}</span>
            {lineCount > 0 && ` · ${lineCount} ${pluralLines(lineCount)}`}
            {artifact.kind !== 'code' && ' · нажмите для просмотра'}
            {dlError && <span className="ml-2 text-destructive">{dlError}</span>}
          </div>
        </div>
        <Eye size={14} className="shrink-0 text-muted-foreground" />
      </button>
      <button
        type="button"
        onClick={onDownload}
        disabled={downloading}
        className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-bubble disabled:opacity-60"
        title="Скачать"
      >
        <Download size={14} />
      </button>
    </div>
  );
}

function pluralLines(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'строка';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'строки';
  return 'строк';
}
